import type React from 'react'
import { Link } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

const SURVEY_TAG_REGEX = /<email>(.*?)<\/email>|<link\s+url="(.*?)">(.*?)<\/link>|<tab\s+id="(\d+)">(.*?)<\/tab>/g

interface TabLinkProps {
  tabId: string
  children: React.ReactNode
}

const TabLink = ({ tabId, children }: TabLinkProps): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    // URL shape: /(Planning|Implementation)/:rcnId/:planId/:tab[/...]
    // Truncate everything after :tab and replace :tab with the requested id.
    const segments = location.pathname.split('/')
    const baseIndex = segments.findIndex(s => s === 'Planning' || s === 'Implementation')
    if (baseIndex === -1) return
    const tabIndex = baseIndex + 3
    const truncated = segments.slice(0, tabIndex + 1)
    truncated[tabIndex] = tabId
    navigate(truncated.join('/'))
  }

  return (
    <Link href="#" onClick={handleClick}>{children}</Link>
  )
}

export function parseSurveyText (text: string): React.ReactNode {
  if (text == null || text === '') return text

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = SURVEY_TAG_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    if (match[1] != null) {
      // <email>addr</email>
      parts.push(
        <Link key={key++} href={`mailto:${match[1]}`}>{match[1]}</Link>
      )
    } else if (match[2] != null && match[3] != null) {
      // <link url="...">text</link>
      parts.push(
        <Link key={key++} href={match[2]} target="_blank" rel="noopener noreferrer">{match[3]}</Link>
      )
    } else if (match[4] != null && match[5] != null) {
      // <tab id="N">text</tab> — replaces the last segment of the current URL with N
      parts.push(
        <TabLink key={key++} tabId={match[4]}>{match[5]}</TabLink>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex === 0) return text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <>{parts}</>
}
